import React from 'react';
import type { FC } from 'react';
import { Layout, Menu } from 'antd';
import {
  AppstoreOutlined, HomeOutlined, DatabaseOutlined, TagsOutlined, CalendarOutlined, SyncOutlined,
  DeleteOutlined, FileSearchOutlined, TeamOutlined, ShopOutlined, SettingOutlined, FileProtectOutlined,
  UserOutlined, FileDoneOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
}

const Sidebar: FC<SidebarProps> = ({ collapsed }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);

  // Xác định key hiện tại dựa trên pathname
  const getCurrentKey = () => {
    const pathname = location.pathname;
    if (pathname === '/') return 'dashboard';
    if (pathname === '/assets') return 'assets';
    if (pathname === '/asset-categories') return 'asset-categories';
    if (pathname === '/employees') return 'employees';
    if (pathname === '/locations') return 'locations';
    if (pathname === '/maintenance-types') return 'maintenance-types';
    if (pathname === '/maintenance-campaigns') return 'maintenance-campaigns';
    if (pathname === '/companies') return 'companies';
    if (pathname === '/suppliers') return 'suppliers';
    if (pathname === '/users') return 'users';
    if (pathname.startsWith('/asset-transfers')) return 'asset-transfer';
    if (pathname.startsWith('/disposals')) return 'disposal';
    return 'dashboard';
  };

  // Cập nhật openKeys khi route thay đổi
  React.useEffect(() => {
    const pathname = location.pathname;
    const settingsPages = ['/asset-categories', '/suppliers', '/employees', '/locations', '/maintenance-types', '/companies', '/users'];
    if (settingsPages.includes(pathname)) {
      setOpenKeys(['settings']);
    }
  }, [location.pathname]);

  // Tạo children cho menu settings theo role
  const settingsChildren = [
    { key: 'asset-categories', icon: <TagsOutlined />, label: 'Danh mục tài sản' },
    { key: 'suppliers', icon: <ShopOutlined />, label: 'Nhà cung cấp' },
    ...(user?.role === 'super_admin' ? [{ key: 'companies', icon: <AppstoreOutlined />, label: 'Công ty' }] : []),
    { key: 'employees', icon: <TeamOutlined />, label: 'Nhân viên' },
    { key: 'locations', icon: <EnvironmentOutlined />, label: 'Vị trí' },
    ...(user?.role === 'super_admin' ? [{ key: 'users', icon: <UserOutlined />, label: 'Người dùng' }] : []),
    { key: 'maintenance-types', icon: <FileProtectOutlined />, label: 'Loại bảo trì' },
  ];
  return (
    <Sider
      collapsed={collapsed}
      width={240}
      style={{ background: '#fff', minHeight: '100vh' }}
    >
      <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', padding: 16 }}>
        {/* Đổi icon thành logo_asm.png */}
        <img
          src={process.env.PUBLIC_URL + '/logo_notext.png'}
          alt="Logo ASM"
          style={{ height: 36, width: 36, objectFit: 'contain', borderRadius: 8, marginRight: collapsed ? 0 : 12 }}
        />
        {!collapsed && <span style={{ fontWeight: 700, fontSize: 20, color: '#444444' }}>Quản lý tài sản</span>}
      </div>
      <Menu
        mode="inline"
        selectedKeys={[getCurrentKey()]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        style={{ borderRight: 0 }}
        onClick={({ key }: { key: string }) => {
          if (key === 'asset-categories') navigate('/asset-categories');
          if (key === 'assets') navigate('/assets');
          if (key === 'dashboard') navigate('/');
          if (key === 'employees') navigate('/employees');
          if (key === 'locations') navigate('/locations');
          if (key === 'maintenance-types') navigate('/maintenance-types');
          if (key === 'maintenance-campaigns') navigate('/maintenance-campaigns');
          if (key === 'companies') navigate('/companies');
          if (key === 'suppliers') navigate('/suppliers');
          if (key === 'users') navigate('/users');
          if (key === 'inventory-audit') navigate('/inventory-audit');
          if (key === 'asset-transfer') navigate('/asset-transfers');
          if (key === 'disposal') navigate('/disposal');
          if (key === 'system-audit') navigate('/system-audit');
        }}
        items={[
          { key: 'dashboard', icon: <HomeOutlined />, label: 'Dashboard' },
          { key: 'assets', icon: <DatabaseOutlined />, label: 'Danh sách tài sản' },
          ...((user?.role !== 'employee_user' || (user?.managed_location_ids && user.managed_location_ids.length > 0)) ? [
            { key: 'maintenance-campaigns', icon: <CalendarOutlined />, label: 'Tác nghiệp định kỳ' },
            { key: 'inventory-audit', icon: <FileSearchOutlined />, label: 'Kiểm kê tài sản' },
          ] : []),
          { key: 'asset-transfer', icon: <SyncOutlined />, label: 'Điều chuyển tài sản' },
          { key: 'disposal', icon: <DeleteOutlined />, label: 'Thanh lý tài sản' },
          ...(user?.role === 'super_admin' ? [{ key: 'system-audit', icon: <FileDoneOutlined />, label: 'Nhật ký hệ thống' }] : []),
          ...(user?.role !== 'employee_user' ? [
            {
              key: 'settings',
              icon: <SettingOutlined />,
              label: 'Cài đặt',
              children: settingsChildren
            }
          ] : [])
        ]}
      />
    </Sider>
  );
};

export default Sidebar; 